/**
 * Cola offline de ventas: cada venta se persiste en IndexedDB antes de enviarse y el reintento es
 * inofensivo, porque `uuid_cliente` es único por venta. Ante una falla, `clasificar-error.ts`
 * decide entre reintentar, cerrarla como ya registrada, o pasarla a rechazadas.
 */
import { openDB, type IDBPDatabase } from 'idb';
import type { DescuentoDeVenta, LineaMostrador } from '@tanteo/compartido';
import { registrarVentaRpc, type PagoDeVenta, type ResultadoVenta } from './api';
import { clasificarFalla } from './clasificar-error';

interface VentaPendiente {
  uuidCliente: string;
  negocioId: string;
  cuentaId: string | null;
  /** Reparto de un cobro mixto: sube igual que la venta, para que el día cuadre al reintentar. */
  pagos?: PagoDeVenta[] | null;
  /** El descuento a la venta completa; los de producto viajan en cada línea. */
  descuento?: DescuentoDeVenta | null;
  /** El número del pedido del que sale; sin él, es una venta suelta. */
  pedidoNumero?: number | null;
  terceroId: string | null;
  lineas: LineaMostrador[];
  fecha: string;
  hora: string;
  creadaEn: number;
  intentos: number;
  /** Momento del último intento: de aquí sale la espera creciente. */
  ultimoIntento?: number;
}

export interface VentaRechazada extends VentaPendiente {
  motivo: string;
  rechazadaEn: number;
}

/** Cuántas ventas esperan y cuántas quedaron rechazadas esperando decisión del cajero. */
export interface EstadoCola {
  pendientes: number;
  rechazadas: number;
}

type Oyente = (estado: EstadoCola) => void;

const PENDIENTES = 'ventas-pendientes';
const RECHAZADAS = 'ventas-rechazadas';

let dbPromesa: Promise<IDBPDatabase> | null = null;
const oyentes = new Set<Oyente>();
let drenando = false;

function db(): Promise<IDBPDatabase> {
  // Versión 2: entra `ventas-rechazadas`. La 1 solo tenía pendientes, así que una pestaña vieja
  // sigue leyendo su almacén sin problema y la migración no toca ninguna venta guardada.
  dbPromesa ??= openDB('tanteo-pos', 2, {
    upgrade(base) {
      if (!base.objectStoreNames.contains(PENDIENTES)) {
        base.createObjectStore(PENDIENTES, { keyPath: 'uuidCliente' });
      }
      if (!base.objectStoreNames.contains(RECHAZADAS)) {
        base.createObjectStore(RECHAZADAS, { keyPath: 'uuidCliente' });
      }
    },
  });
  return dbPromesa;
}

async function estado(): Promise<EstadoCola> {
  const base = await db();
  return { pendientes: await base.count(PENDIENTES), rechazadas: await base.count(RECHAZADAS) };
}

async function notificar(): Promise<void> {
  const e = await estado();
  for (const o of oyentes) o(e);
}

export function alCambiarCola(oyente: Oyente): () => void {
  oyentes.add(oyente);
  void estado().then(oyente);
  return () => oyentes.delete(oyente);
}

/** Las escrituras que guardan una venta esperan al disco: en `relaxed`, el modo por omisión de Chrome, el
 * `complete` llega con el dato aún en el búfer del sistema y un corte de luz puede perderlo. */
const EN_DISCO: IDBTransactionOptions = { durability: 'strict' };

async function encolarVenta(venta: Omit<VentaPendiente, 'creadaEn' | 'intentos'>): Promise<void> {
  const tx = (await db()).transaction(PENDIENTES, 'readwrite', EN_DISCO);
  await Promise.all([tx.store.put({ ...venta, creadaEn: Date.now(), intentos: 0 }), tx.done]);
  await notificar();
}

async function quitar(uuidCliente: string): Promise<void> {
  await (await db()).delete(PENDIENTES, uuidCliente);
  await notificar();
}

/** Deja constancia del intento para espaciar el siguiente. */
async function anotarIntento(v: VentaPendiente): Promise<void> {
  await (await db()).put(PENDIENTES, { ...v, intentos: v.intentos + 1, ultimoIntento: Date.now() });
  await notificar();
}

/** Saca la venta de la cola y la guarda como rechazada, con el motivo ya en español. */
async function rechazar(v: VentaPendiente, motivo: string): Promise<void> {
  const rechazada: VentaRechazada = { ...v, motivo, rechazadaEn: Date.now() };
  // En una sola transacción: la venta pasa de un almacén al otro sin quedar en ninguno ni en los dos.
  const tx = (await db()).transaction([RECHAZADAS, PENDIENTES], 'readwrite', EN_DISCO);
  await Promise.all([
    tx.objectStore(RECHAZADAS).put(rechazada),
    tx.objectStore(PENDIENTES).delete(v.uuidCliente),
    tx.done,
  ]);
  await notificar();
}

export async function listarRechazadas(): Promise<VentaRechazada[]> {
  const filas = (await (await db()).getAll(RECHAZADAS)) as VentaRechazada[];
  return filas.sort((a, b) => a.rechazadaEn - b.rechazadaEn);
}

/** El cajero decide descartarla: es la única forma de que una venta salga del sistema. */
export async function descartarRechazada(uuidCliente: string): Promise<void> {
  await (await db()).delete(RECHAZADAS, uuidCliente);
  await notificar();
}

/** Espera creciente: 0s, 30s, 1m, 2m, 4m… con techo de 5 minutos. */
function toca(v: VentaPendiente, ahora: number): boolean {
  if (!v.ultimoIntento) return true;
  const espera = Math.min(30_000 * 2 ** Math.max(0, v.intentos - 1), 300_000);
  return ahora - v.ultimoIntento >= espera;
}

function motivoDe(error: unknown): string {
  return error instanceof Error ? error.message : 'El servidor rechazó la venta.';
}

/**
 * Persiste la venta y trata de enviarla: el resultado si el servidor la aceptó, `null` si quedó en
 * cola. Un rechazo aquí se lanza al cajero; el almacén de rechazadas es para las de `drenar`.
 */
export async function registrarOEncolar(
  venta: Omit<VentaPendiente, 'creadaEn' | 'intentos'>
): Promise<ResultadoVenta | null> {
  await encolarVenta(venta);
  try {
    const resultado = await registrarVentaRpc({
      negocioId: venta.negocioId,
      uuidCliente: venta.uuidCliente,
      cuentaId: venta.cuentaId,
      pagos: venta.pagos ?? null,
      descuento: venta.descuento ?? null,
      pedidoNumero: venta.pedidoNumero ?? null,
      terceroId: venta.terceroId,
      lineas: venta.lineas,
      fecha: venta.fecha,
      hora: venta.hora,
    });
    await quitar(venta.uuidCliente);
    return resultado;
  } catch (error) {
    const clase = clasificarFalla(error);
    if (clase === 'reintentable') return null; // se queda en la cola; `drenar` la enviará
    await quitar(venta.uuidCliente);
    if (clase === 'ya-registrada') return null; // el servidor ya la tenía
    throw error; // definitivo: el cajero lo ve ahora, con las líneas todavía en pantalla
  }
}

/** Envía las encoladas en orden de creación. Segura de llamar en paralelo. */
export async function drenar(): Promise<void> {
  if (drenando || !navigator.onLine) return;
  drenando = true;
  try {
    const base = await db();
    const pendientes = ((await base.getAll(PENDIENTES)) as VentaPendiente[]).sort(
      (a, b) => a.creadaEn - b.creadaEn
    );
    const ahora = Date.now();
    for (const v of pendientes) {
      if (!toca(v, ahora)) continue;
      try {
        await registrarVentaRpc({
          negocioId: v.negocioId,
          uuidCliente: v.uuidCliente,
          cuentaId: v.cuentaId,
          pagos: v.pagos ?? null,
          descuento: v.descuento ?? null,
          pedidoNumero: v.pedidoNumero ?? null,
          terceroId: v.terceroId,
          lineas: v.lineas,
          fecha: v.fecha,
          hora: v.hora,
        });
        await quitar(v.uuidCliente);
      } catch (error) {
        const clase = clasificarFalla(error);
        if (clase === 'ya-registrada') {
          await quitar(v.uuidCliente);
          continue;
        }
        if (clase === 'reintentable') {
          // Se corta aquí para no romper el orden de los consecutivos ni martillar al servidor.
          await anotarIntento(v);
          break;
        }
        await rechazar(v, motivoDe(error));
      }
    }
  } finally {
    drenando = false;
  }
}

export function iniciarSincronizador(): void {
  window.addEventListener('online', () => void drenar());
  setInterval(() => void drenar(), 30_000);
  void drenar();
}
